import { combineReducers, configureStore } from "@reduxjs/toolkit";

import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";

import { setUnauthorizedHandler } from "../axios/axios";
import authReducer, { authActions } from "./storage/authSlice";
import clientReducer from "./storage/clientSlice";
import projectReducer from "./storage/projectSlice";
import transactionSlice from "./storage/transactionSlice";
import timeTrackingSlice from "./storage/timetrackingSlice";
import dashboardSlice from "./storage/dashboardSlice";
import proposalAndContractSlice from "./storage/proposalAndContractSlice";
import statementSlice from "./storage/statementSlice";
import invoiceSlice from "./storage/invoiceSlice";
import notificationSlice from "./storage/notificationSlice";
import deleteUserAcountSlice from "./storage/deleteUserAcountSlice";
import adminSlice from "./storage/adminSlice";
import noteReducer from "./storage/noteSlice";
import taskReducer from "./storage/TaskSlice";
import faqsReducer from "./storage/faqSlice";
import subscriptionReducer from "./storage/subscriptionSlice";
import meetingReducer from "./storage/meetingsSlice";
import meetingEventReducer from "./storage/meetingEventSlice";

const appReducer = combineReducers({
  auth: authReducer,
  dashboard: dashboardSlice,
  tasks: taskReducer,
  clients: clientReducer,
  project: projectReducer,
  transaction: transactionSlice,
  timeTracking: timeTrackingSlice,
  proposalAndContract: proposalAndContractSlice,
  statements: statementSlice,
  invoices: invoiceSlice,
  notifications: notificationSlice,
  deleteUserAccount: deleteUserAcountSlice,
  admin: adminSlice,
  notes: noteReducer,
  faqs: faqsReducer,
  subscriptions: subscriptionReducer,
  meetings: meetingReducer,
  meetingEvents: meetingEventReducer,
});

const rootReducer = (state, action) => {
  if (action.type === "authentication/logout") {
    storage.removeItem("persist:persist-root");
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

const persistConfig = {
  key: "persist-root",
  storage,
  // whitelist: ["authReducer", "signUpReducer"],
  // blacklist: [],
  version: 14, // increase this when you change state structure
  stateReconciler: autoMergeLevel2,
  migrate: async (state) => {
    if (!state) return null;

    const newState = { ...state };

    const authData = newState.auth && newState.auth.data;

    if (authData && !authData.plan) {
      authData.plan = 'free';
    }

    if (authData && !authData.proposalContractSentCount) {
      authData.proposalContractSentCount = 0;
    }

    if (authData && !authData.statementDownloads) {
      authData.statementDownloads = 0;
    }

    if (authData && !authData.timeTrackingReportDownloads) {
      authData.timeTrackingReportDownloads = 0;
    }

    if (authData && !authData.invoiceSentCount) {
      authData.invoiceSentCount = 0;
    }

    if (!newState.subscriptions) {
      newState.subscriptions = {};
    }

    if (!newState.subscriptions.isPaymentProcessing) {
      newState.subscriptions.isPaymentProcessing = false;
    }

    if (
      !newState.subscriptions.priceLoading ||
      !newState.subscriptions.priceMonthly ||
      !newState.subscriptions.priceYearly
    ) {
      newState.subscriptions.priceLoading = false;
      newState.subscriptions.priceMonthly = 2.99;
      newState.subscriptions.priceYearly = 29;
    }

    if (!newState.meetings) {
      newState.meetings = {
        status: "upcoming",
        sortBy: "scheduledAt",
        orderBy: "asc",
        googleConnected: false,
        googleName: "",
        email: "",
      };
    }

     if (!newState.meetingEvents) {
       newState.meetingEvents = {
         sortBy: "title",
         orderBy: "asc",
       };
     }

    if (!newState.tasks) {
      newState.tasks = {};
    }

    if (!newState.tasks.activeTask) {
      newState.tasks.activeTask = null;
    }

    return newState;
  },
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: process.env.NODE_ENV !== "production",
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: process.env.NODE_ENV !== "production",
});

const persistor = persistStore(store);

// wire the axios client to the store without axios importing the store back
setUnauthorizedHandler(() => store.dispatch(authActions.logout()));

export { store, persistor };
